import type {
  UndoFromPortInterface,
  UndoToPortInterface,
} from 'gojs/goDiagram';
import type { ConnectionInterface } from 'store/connections/types';
import type { UnitTypes } from 'unitData/types';
import type {
  GoJSInterface,
  FlowInterface,
  FlowPropertiesInterface,
  UnitInfoInterface,
  PortMode,
  EditMultipleUnitLocationsInterface,
  EDIT_LAYOUTS,
  PanelType,
} from './types';
import {
  ADD_UNIT,
  REMOVE_UNIT,
  EDIT_UNIT_FLOW,
  EDIT_FLOW_PROPERTIES,
  EDIT_UNIT_INFO,
  EDIT_PORT,
  EDIT_LAYOUT,
  TOGGLE_MIRRORING,
  EDIT_MULTIPLE_UNIT_LOCATIONS,
  REPLACE_UNIT,
  INSERT_ON_CONNECTION,
  HIGHLIGHT_NODES,
  ADD_STORAGE_TO_SCREEN_OUTPUTS,
} from './types';

// More permissive type that accepts both objects with index signatures
// and regular interfaces
export type AddUnitDataInterface = Record<string, unknown> | object;

export interface AddUnitActionInterface {
  [extraProps: string]: unknown;
  type: typeof ADD_UNIT;
  payload: {
    layout: GoJSInterface;
    unitInfoData: AddUnitDataInterface;
    key?: string;
    outputPortKeys?: UndoFromPortInterface[];
    inputPortKeys?: UndoToPortInterface[];
    unitInfo?: UnitInfoInterface;
    inputFlow?: FlowInterface;
    flowProperties?: FlowPropertiesInterface;
    panels?: Record<PanelType, string>;
  };
}

export function addUnit(
  layout: GoJSInterface,
  unitInfoData: AddUnitDataInterface,
  key?: string,
  outputPortKeys?: UndoFromPortInterface[],
  inputPortKeys?: UndoToPortInterface[],
  unitInfo?: UnitInfoInterface,
  inputFlow?: FlowInterface,
  flowProperties?: FlowPropertiesInterface,
  panels?: Record<PanelType, string>,
): AddUnitActionInterface {
  return {
    type: ADD_UNIT,
    payload: {
      layout,
      unitInfoData,
      key,
      outputPortKeys,
      inputPortKeys,
      unitInfo,
      inputFlow,
      flowProperties,
      panels,
    },
  };
}

export interface ReplaceUnitInterface {
  type: typeof REPLACE_UNIT;
  payload: { key: string; type: UnitTypes; variantId?: number };
}

export function replaceUnitAction(
  key: string,
  type: UnitTypes,
  variantId?: number | undefined,
) {
  return {
    type: REPLACE_UNIT,
    payload: { key, type, variantId },
  };
}

export interface RemoveUnitsActionInterface {
  [extraProps: string]: unknown;
  type: typeof REMOVE_UNIT;
  payload: string[];
}

export function removeUnits(keys: string[]): RemoveUnitsActionInterface {
  return {
    type: REMOVE_UNIT,
    payload: keys,
  };
}

export interface EditUnitInfoActionInterface {
  [extraProps: string]: unknown;
  type: typeof EDIT_UNIT_INFO;
  payload: { unitKey: string | null; unitInfo: UnitInfoInterface };
}

export function editUnitInfo(
  unitKey: string,
  unitInfo: UnitInfoInterface,
): EditUnitInfoActionInterface {
  return {
    type: EDIT_UNIT_INFO,
    payload: { unitKey: unitKey, unitInfo: unitInfo },
  };
}

export interface EditUnitFlowActionInterface {
  [extraProps: string]: unknown;
  type: typeof EDIT_UNIT_FLOW;
  payload: { unitKey: string; inputFlow: FlowInterface };
}
export function editUnitFlow(
  unitKey: string,
  inputFlow: FlowInterface,
): EditUnitFlowActionInterface {
  return {
    type: EDIT_UNIT_FLOW,
    payload: { unitKey: unitKey, inputFlow: inputFlow },
  };
}

export interface EditFlowPropertiesActionInterface {
  [extraProps: string]: unknown;
  type: typeof EDIT_FLOW_PROPERTIES;
  payload: {
    unitKey: string;
    flowProperties: FlowPropertiesInterface;
  };
}
export function editFlowProperties(
  unitKey: string,
  flowProperties: FlowPropertiesInterface,
): EditFlowPropertiesActionInterface {
  return {
    type: EDIT_FLOW_PROPERTIES,
    payload: {
      unitKey: unitKey,
      flowProperties: flowProperties,
    },
  };
}
export interface EditUnitLayoutActionInterface {
  [extraProps: string]: unknown;
  type: typeof EDIT_LAYOUT;
  payload: {
    unitKey: string;
    layout: GoJSInterface;
  };
}

export function editLayout(
  unitKey: string,
  layout: GoJSInterface,
): EditUnitLayoutActionInterface {
  return {
    type: EDIT_LAYOUT,
    payload: {
      unitKey: unitKey,
      layout: layout,
    },
  };
}

export interface EditUnitsLayoutActionInterface {
  [extraProps: string]: unknown;
  type: typeof EDIT_LAYOUTS;
  payload: {
    unitKey: string;
    layout: GoJSInterface;
  }[];
}

export interface EditMultipleUnitLocationsActionInterface {
  [extraProps: string]: unknown;
  type: typeof EDIT_MULTIPLE_UNIT_LOCATIONS;
  payload: {
    unitLocationsToEdit: EditMultipleUnitLocationsInterface[];
  };
}
export function editMultipleUnitLocations(
  unitLocationsToEdit: EditMultipleUnitLocationsInterface[],
): EditMultipleUnitLocationsActionInterface {
  return {
    type: EDIT_MULTIPLE_UNIT_LOCATIONS,
    payload: {
      unitLocationsToEdit: unitLocationsToEdit,
    },
  };
}

export interface ToggleMirroringActionInterface {
  [extraProps: string]: unknown;
  type: typeof TOGGLE_MIRRORING;
  payload: {
    unitKey: string;
  };
}
export function toggleMirroring(
  unitKey: string,
): ToggleMirroringActionInterface {
  return {
    type: TOGGLE_MIRRORING,
    payload: {
      unitKey: unitKey,
    },
  };
}

export interface EditPortsActionInterface {
  [extraProps: string]: unknown;
  type: typeof EDIT_PORT;
  payload: {
    unitKey: string;
    portMode: PortMode;
    diff: number;
  };
}

export function editPort(
  unitKey: string,
  portMode: PortMode,
  diff: number,
): EditPortsActionInterface {
  return {
    type: EDIT_PORT,
    payload: { unitKey, portMode, diff },
  };
}

export interface InsertOnConnectionActionInterface {
  [extraProps: string]: unknown;
  type: typeof INSERT_ON_CONNECTION;
  payload: {
    unitType: UnitTypes;
    connection: ConnectionInterface;
    loc: string;
    variantId?: number;
  };
}

export function insertOnConnection(
  unitType: UnitTypes,
  connection: ConnectionInterface,
  loc: string,
  variantId?: number,
) {
  return {
    type: INSERT_ON_CONNECTION,
    payload: { unitType, connection, loc, variantId },
  };
}

export interface HighlightNodesActionInterface {
  [extraProps: string]: unknown;
  type: typeof HIGHLIGHT_NODES;
  payload: {
    unitKeys: string[];
  };
}

export function highlightNodes(
  unitKeys: string[],
): HighlightNodesActionInterface {
  return {
    type: HIGHLIGHT_NODES,
    payload: { unitKeys },
  };
}

export interface AddStorageToScreenOutputsActionInterface {
  [extraProps: string]: unknown;
  type: typeof ADD_STORAGE_TO_SCREEN_OUTPUTS;
  payload: { unitKey: string };
}

export function addStorageToScreenOutputs(
  unitKey: string,
): AddStorageToScreenOutputsActionInterface {
  return {
    type: ADD_STORAGE_TO_SCREEN_OUTPUTS,
    payload: { unitKey },
  };
}
